import { Outlet, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, LayoutDashboard, FileText, Upload, Settings, LogOut, Users, Activity, AlertTriangle, Database, Download, UserCheck, Trash2, Bell, FolderKanban, BarChart3, Building2, ClipboardList, KeyRound, UserCog, ChevronDown, ChevronRight, Search, X, FileSpreadsheet, Globe, Repeat } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import HubStatusDot from '@/components/HubStatusDot';
import axios from 'axios';
import { toast } from 'sonner';
import privaxiaLogo from '@/assets/privaxia-logo.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tenantSettings, setTenantSettings] = useState({ integration_platforms: [] });

  // Fetch tenant settings on mount
  useEffect(() => {
    const fetchTenantSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/tenants/current/settings`, { withCredentials: true });
        setTenantSettings(response.data);
      } catch (error) {
        console.error('Error fetching tenant settings:', error);
      }
    };
    if (user) {
      fetchTenantSettings();
    }
  }, [user]);

  // Check if an integration platform is active
  const hasIntegration = (platformId) => {
    return tenantSettings.integration_platforms?.includes(platformId) || false;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Sessão terminada');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // Menu items with submenus
  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' : user?.role === 'operador' ? '/operador' : user?.role === 'dpo' ? '/dpo' : user?.role === 'tenant_admin' ? '/admin' : '/dashboard', roles: ['titular', 'operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
    { icon: Building2, label: 'Gestão de Tenants', path: '/tenants', roles: ['super_admin', 'admin'] },
    { 
      icon: UserCheck, 
      label: 'Titulares', 
      id: 'titulares',
      roles: ['titular', 'operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'],
      submenu: [
        { icon: UserCheck, label: 'Lista de Titulares', path: '/titulares', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: FileText, label: 'Consentimentos', path: '/consents', roles: ['titular', 'operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: Trash2, label: 'Dir. Esquecimento', path: '/rtbf', roles: ['dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: Upload, label: 'Importar/Exportar', path: '/import-export', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
      ]
    },
    { 
      icon: FolderKanban, 
      label: 'Ações & Campanhas', 
      id: 'marketing',
      roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'],
      submenu: [
        { icon: FolderKanban, label: 'Grupos & Campanhas', path: '/groups', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: Activity, label: 'Ações de Marketing', path: '/marketing-actions', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: ClipboardList, label: 'Compliance (DPIA, SCC, Docs)', path: '/compliance', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: FileSpreadsheet, label: 'Validação RGPD Autoline', path: '/autoline', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'], requiresIntegration: 'autoline' },
      ]
    },
    { 
      icon: BarChart3, 
      label: 'Analytics', 
      id: 'analytics',
      roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'],
      submenu: [
        { icon: BarChart3, label: 'Dashboard', path: '/analytics', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
        { icon: Activity, label: 'Logs de Auditoria', path: '/admin/audit-logs', roles: ['admin', 'dpo', 'super_admin', 'tenant_admin'] },
      ]
    },
    { icon: Bell, label: 'Alertas Expiração', path: '/expiring-consents', roles: ['operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
    { icon: Download, label: 'Exportar Dados', path: '/export-data', roles: ['titular', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
    { 
      icon: Users, 
      label: 'Utilizadores', 
      id: 'users',
      roles: ['admin', 'super_admin', 'tenant_admin', 'dpo'],
      submenu: [
        { icon: Users, label: 'Gestão Utilizadores', path: '/admin/users', roles: ['admin', 'super_admin', 'tenant_admin'] },
        { icon: UserCog, label: 'Gestão de Roles', path: '/role-management', roles: ['admin', 'super_admin', 'tenant_admin'] },
        { icon: KeyRound, label: 'Permissões', path: '/permissions', roles: ['admin', 'dpo', 'super_admin', 'tenant_admin'] },
      ]
    },
    { icon: AlertTriangle, label: 'Violações de Dados', path: '/data-breaches', roles: ['dpo', 'admin', 'super_admin', 'tenant_admin'] },
    { icon: ClipboardList, label: 'ROPA', path: '/ropa', roles: ['dpo', 'admin', 'super_admin', 'tenant_admin'] },
    { 
      icon: Database, 
      label: 'Sistema', 
      id: 'system',
      roles: ['admin', 'super_admin'],
      submenu: [
        { icon: Database, label: 'Backups', path: '/admin/backups', roles: ['admin', 'super_admin'] },
        { icon: Activity, label: 'Logs de Login', path: '/admin/login-logs', roles: ['admin', 'super_admin'] },
      ]
    },
    { icon: Settings, label: 'Definições', path: '/settings', roles: ['titular', 'operador', 'dpo', 'admin', 'super_admin', 'tenant_admin'] },
    { icon: Globe, label: 'Definições da Plataforma', path: '/superadmin/platform', roles: ['super_admin', 'admin'] },
  ];

  const filterMenuItems = (items) => {
    return items.filter(item => {
      if (item.submenu) {
        const filteredSubmenu = item.submenu.filter(sub => {
          // Check role permission
          if (!sub.roles.includes(user?.role)) return false;
          // Check integration requirement (super_admin always sees all integrations)
          if (sub.requiresIntegration && user?.role !== 'super_admin' && !hasIntegration(sub.requiresIntegration)) return false;
          return true;
        });
        return item.roles.includes(user?.role) && filteredSubmenu.length > 0;
      }
      // Check integration requirement for top-level items (super_admin always sees all)
      if (item.requiresIntegration && user?.role !== 'super_admin' && !hasIntegration(item.requiresIntegration)) return false;
      return item.roles.includes(user?.role);
    });
  };

  const filteredMenuItems = filterMenuItems(menuItems);

  const isSubmenuActive = (submenu) => {
    return submenu?.some(item => location.pathname === item.path);
  };

  const renderMenuItem = (item) => {
    if (item.submenu) {
      const filteredSubmenu = item.submenu.filter(sub => {
        // Check role permission
        if (!sub.roles.includes(user?.role)) return false;
        // Check integration requirement (super_admin always sees all integrations)
        if (sub.requiresIntegration && user?.role !== 'super_admin' && !hasIntegration(sub.requiresIntegration)) return false;
        return true;
      });
      const isExpanded = expandedMenus[item.id] || isSubmenuActive(filteredSubmenu);
      
      return (
        <div key={item.id}>
          <Button
            variant="ghost"
            className="w-full justify-between text-white hover:bg-white/10 hover:text-white"
            onClick={() => toggleMenu(item.id)}
          >
            <div className="flex items-center">
              <item.icon className="w-5 h-5" />
              {sidebarOpen && <span className="ml-3">{item.label}</span>}
            </div>
            {sidebarOpen && (
              isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            )}
          </Button>
          {isExpanded && sidebarOpen && (
            <div className="ml-4 mt-1 space-y-1 border-l border-white/20 pl-2">
              {filteredSubmenu.map((subItem) => (
                <Button
                  key={subItem.path}
                  variant="ghost"
                  className={`w-full justify-start text-white/80 hover:bg-white/10 hover:text-white text-sm ${
                    location.pathname === subItem.path ? 'bg-white/10 text-white' : ''
                  }`}
                  onClick={() => navigate(subItem.path)}
                >
                  <subItem.icon className="w-4 h-4" />
                  <span className="ml-2">{subItem.label}</span>
                </Button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Button
        key={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        variant="ghost"
        className={`w-full justify-start text-white hover:bg-white/10 hover:text-white ${
          location.pathname === item.path ? 'bg-white/10' : ''
        }`}
        onClick={() => navigate(item.path)}
      >
        <item.icon className="w-5 h-5" />
        {sidebarOpen && <span className="ml-3">{item.label}</span>}
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#0F172A] text-white h-screen transition-all duration-300 fixed left-0 top-0 z-40 flex flex-col`}>
          <div className="p-6 pb-2 shrink-0">
            <div className="flex items-center gap-3 mb-8">
              <img src={privaxiaLogo} alt="Privaxia Logo" className="w-10 h-10 object-contain" />
              {sidebarOpen && (
                <div>
                  <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Privaxia</h1>
                  <p className="text-xs text-slate-400">Data Privacy & Compliance</p>
                </div>
              )}
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 pb-4 space-y-2">
            {filteredMenuItems.map((item) => renderMenuItem(item))}
          </nav>

          <div className="shrink-0 p-6 border-t border-white/10">
            <div className="mb-4">
              {sidebarOpen && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#10B981] flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                    <p className="text-xs text-[#10B981] uppercase font-semibold mt-1">
                      {user?.is_dpo_global ? 'DPO Global' : user?.role}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {user?.is_dpo_global && sidebarOpen && (
              <Button
                data-testid="switch-tenant-button"
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10 hover:text-white mb-2"
                onClick={() => navigate('/dpo/tenants')}
              >
                <Repeat className="w-5 h-5" />
                <span className="ml-3">Trocar organização</span>
              </Button>
            )}
            <Button
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start text-white hover:bg-white/10 hover:text-white"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" />
              {sidebarOpen && <span className="ml-3">Sair</span>}
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300 flex flex-col min-h-screen`}>
          <div className="flex items-center justify-between gap-4 p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            {/* Quick Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="global-search"
                placeholder="Pesquisa rápida... (menu, páginas)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-8"
                onFocus={() => setSearchOpen(true)}
              />
              {searchTerm && (
                <button 
                  onClick={() => { setSearchTerm(''); setSearchOpen(false); }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              {/* Search Results Dropdown */}
              {searchOpen && searchTerm && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  {filteredMenuItems
                    .flatMap(item => {
                      if (item.submenu) {
                        return item.submenu.filter(sub => 
                          sub.label.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map(sub => ({ ...sub, parent: item.label }));
                      }
                      if (item.label.toLowerCase().includes(searchTerm.toLowerCase())) {
                        return [item];
                      }
                      return [];
                    })
                    .slice(0, 8)
                    .map((result, idx) => (
                      <button
                        key={idx}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => {
                          navigate(result.path);
                          setSearchTerm('');
                          setSearchOpen(false);
                        }}
                      >
                        {result.icon && <result.icon className="w-4 h-4 text-slate-500" />}
                        <span className="text-sm text-[#0F172A]">{result.label}</span>
                        {result.parent && (
                          <span className="text-xs text-slate-400 ml-auto">{result.parent}</span>
                        )}
                      </button>
                    ))
                  }
                  {filteredMenuItems
                    .flatMap(item => item.submenu ? item.submenu : [item])
                    .filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
                    .length === 0 && (
                    <div className="px-4 py-3 text-sm text-slate-500 text-center">
                      Nenhum resultado encontrado
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {['super_admin', 'admin'].includes(user?.role) && <HubStatusDot />}
            <NotificationBell />
          </div>
          <div className="flex-1 p-8">
            <Outlet context={{ user }} />
          </div>
          
          {/* Footer */}
          <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-8 py-4">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Privaxia - Data Privacy & Compliance</span>
              <span>
                Designed by <a href="https://lptec.pt" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">LPtec.pt</a> and Powered by <a href="https://cogniseg.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Cogniseg.com</a>
              </span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;